import queryString from 'query-string';
import { matchPath } from 'react-router-dom';
import { ensureBaseURL, removeBaseURL } from '@philips-internal/oswald-common-utils';
import { createURLSearchParams, getPathParams, getScrollElement, isContentfulString, noop, resolvePath } from '@philips-internal/oswald-ui-utils';
import { ConfigurationService } from '../configuration-service/index.js';
const SERVICE_NOT_INITIALIZED_MESSAGE = 'Service not initialized';
function historyError(message) {
    return new Error(`[History Service]: ${message}`);
}
function createHistoryService() {
    let initialized = false;
    let history;
    let location;
    let popped = false;
    let currentParams = {};
    let currentMatch;
    const pathChangeListeners = new Set();
    const matchListeners = new Set();
    const hasBeenInitialized = () => {
        return initialized;
    };
    const initialize = (configuration) => {
        history = configuration.history;
        location = configuration.location;
        initialized = true;
    };
    const scrollToTop = () => {
        const viewport = getScrollElement();
        viewport.scrollTo({ behavior: 'auto', top: 0 });
    };
    const fireMatchListeners = (match) => {
        return Array.from(matchListeners).map(fn => fn(match));
    };
    const firePathChangeListeners = (pathname) => {
        return Array.from(pathChangeListeners).map(fn => fn(pathname));
    };
    const listenHistoryChange = (fn) => {
        return history?.listen(fn) ?? noop;
    };
    const listenMatch = (fn) => {
        if (matchListeners.has(fn)) {
            throw historyError('Listener already added.');
        }
        matchListeners.add(fn);
        return () => {
            matchListeners.delete(fn);
        };
    };
    const setMatch = (match) => {
        currentParams = match.params;
        currentMatch = match;
        fireMatchListeners(match);
    };
    const historyListener = (update) => {
        const { location } = update;
        // @ts-ignore: `fromPush` is something we add to the Location;
        if (location.fromPush || popped) {
            popped = false;
            return;
        }
        firePathChangeListeners(location.pathname);
    };
    const addListener = (fn) => {
        if (pathChangeListeners.has(fn)) {
            historyError('Listener already added.');
        }
        let unlisten = noop;
        if (pathChangeListeners.size === 0) {
            unlisten = listenHistoryChange(historyListener);
        }
        pathChangeListeners.add(fn);
        return () => {
            pathChangeListeners.delete(fn);
            if (pathChangeListeners.size === 0) {
                unlisten();
            }
        };
    };
    const push = (path, params, options) => {
        if (!initialized) {
            throw historyError(SERVICE_NOT_INITIALIZED_MESSAGE);
        }
        const pathParams = {
            ...currentParams,
            ...params,
        };
        // Must remove the wildcard `*` from the pathParams, otherwise it might cause problems with the URL formation;
        if ('*' in pathParams) {
            delete pathParams['*'];
        }
        const resolvedPath = resolvePath(path, pathParams);
        const base = ConfigurationService.getBaseURL();
        const pathname = ensureBaseURL(resolvedPath, base);
        const promises = firePathChangeListeners(pathname);
        const searchParams = createURLSearchParams(options?.searchParams).toString();
        void Promise.all(promises).then(() => {
            const to = {
                // @ts-ignore: Adding `fromPush` directly, so we can obtain it later;
                fromPush: true,
                pathname,
                search: isContentfulString(searchParams) ? `${searchParams}` : '',
                state: {
                    from: location?.pathname,
                },
            };
            const state = { ...options?.state };
            history?.push(to, state);
            if (options?.scrollToTop ?? true) {
                scrollToTop();
            }
        });
    };
    const back = (options = { scrollToTop: true }) => {
        if (!initialized) {
            throw historyError(SERVICE_NOT_INITIALIZED_MESSAGE);
        }
        // Need to type cast here because `react-router@v6` removed the generics from the Location interface.
        const locationState = location.state;
        const previousPath = locationState?.from ?? '';
        const promises = firePathChangeListeners(previousPath);
        void Promise.all(promises).then(() => {
            popped = true;
            history?.back();
            if (options.scrollToTop) {
                scrollToTop();
            }
        });
    };
    const redirect = (url) => {
        return window.location.href = url;
    };
    const getCurrentRoute = () => {
        return window.location.pathname;
    };
    const getCurrentRouteParams = () => {
        return currentParams;
    };
    const getCurrentRouteQueryParams = () => {
        return queryString.parse(window.location.search);
    };
    const getCurrentRouteState = () => {
        if (!initialized) {
            throw historyError(SERVICE_NOT_INITIALIZED_MESSAGE);
        }
        return history.location.state;
    };
    const getMatch = () => {
        return currentMatch;
    };
    const makeURL = (url) => {
        const base = ConfigurationService.getBaseURL();
        return removeBaseURL(url, base);
    };
    const matchRouteExactWithQueryParam = (routeMatch, route = getCurrentRoute()) => {
        const base = ConfigurationService.getBaseURL();
        const url = removeBaseURL(routeMatch, base);
        let routeMatchNormalized = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        routeMatchNormalized = `^${routeMatchNormalized}$`;
        const routeParams = getPathParams(routeMatchNormalized);
        for (const param of routeParams) {
            routeMatchNormalized = routeMatchNormalized.replace(`:${param}`, '([\\w-+])*');
        }
        const regex = new RegExp(routeMatchNormalized);
        return regex.test(route);
    };
    const matchRouteWith = (routeMatch, route = getCurrentRoute()) => {
        const base = ConfigurationService.getBaseURL();
        const url = removeBaseURL(route, base);
        return matchPath(routeMatch, url) || false;
    };
    return {
        addListener,
        back,
        getCurrentRoute,
        getCurrentRouteParams,
        getCurrentRouteQueryParams,
        getCurrentRouteState,
        getMatch,
        hasBeenInitialized,
        initialize,
        listenHistoryChange,
        listenMatch,
        makeURL,
        matchRouteExactWithQueryParam,
        matchRouteWith,
        push,
        redirect,
        setMatch,
    };
}
const HistoryService = createHistoryService();
export { HistoryService, };
//# sourceMappingURL=history-service.js.map